import {
  AppointmentStatus,
  DepositInvoiceClaimStatus,
  DepositPaymentMode,
  DepositRequestStatus,
  DepositStatus,
  type IDepositRequest,
  type IDepositMetadata,
  ISchedulingEventData,
  MentionResourceType,
  ParticipantType,
  PrincipleCreatedBy,
  TransactionProvider,
  TreatmentPlanStatus,
  isEventable,
  isTreatmentPlanWithBookableStep,
  type IAppointment,
  type IAppointmentRequest,
  type IAutomation,
  type IChecklistItem,
  type IDepositPaymentAttempt,
  type IEvent,
  type IInvoice,
  type IInteractionV2,
  type IPatient,
  type IPractice,
  type IStaffer,
  type ITag,
  type ITreatmentStep,
  type TreatmentStepAutomation,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  FieldValue,
  Firestore,
  asyncForEach,
  isINamedDocument,
  isSameRef,
  snapshot,
  toMoment,
  toTimestamp,
  toNamedDocument,
  type INamedDocument,
  type Timestamp,
  type Transaction,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { TimezoneResolver } from '../../../timezone';
import { Appointment } from '../../appointment/appointment';
import { SchedulingEvent } from '../../appointment/scheduling-event';
import { TreatmentStep } from '../../clinical-charting/treatment/treatment-step';
import { stafferToNamedDoc } from '../../common';
import { Event } from '../../event/event';
import { removeConflictingCandidates } from '../../gap/wait-list-candidate';
import { Patient } from '../../patient/patient';
import {
  DepositInvoice,
  type DepositPaymentProvenance,
  type IStripeDepositPayment,
} from '../../deposit/deposit-invoice';
import { type CreateAppointmentDetails } from '../appointment-details';
import { AppointmentTreatmentPlanAssociator } from '../appointment-treatment-plan-associator';
import {
  convertWaitListDetailsToItem,
  type IWaitListDetails,
} from '../waitlist-details';

interface IDepositInvoiceClaimExists {
  status: DepositInvoiceClaimStatus.Exists;
  invoiceRef: DocumentReference<IInvoice>;
}

interface IDepositInvoiceClaimLeased {
  status: DepositInvoiceClaimStatus.Leased;
}

interface IDepositInvoiceClaimed {
  status: DepositInvoiceClaimStatus.Claimed;
  claimedAt: Timestamp;
}

interface IResolvedStripeDepositPayment {
  payment: IStripeDepositPayment;
  depositRequestRef?: DocumentReference<IDepositRequest>;
}

type DepositInvoiceClaimResult =
  | IDepositInvoiceClaimExists
  | IDepositInvoiceClaimLeased
  | IDepositInvoiceClaimed;

const invoiceClaimLease = moment.duration(5, 'minutes');

export class AppointmentCreator {
  static async addTreatmentPlanAssociation(
    appointment: WithRef<IAppointment>,
    duration: number
  ): Promise<void> {
    const step = await Firestore.getDoc<ITreatmentStep>(
      appointment.treatmentPlan.treatmentStep.ref
    );
    if (step.appointment) {
      throw new Error('Selected Treatment Step already has appointment');
    }
    if (!step.schedulingRules.duration) {
      step.schedulingRules.duration = duration;
    }
    await Firestore.patchDoc(step.ref, {
      ...step,
      appointment: appointment.ref,
    });
  }

  static async syncPlanStatus(
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    const plan = await Appointment.treatmentPlan(appointment);
    if (plan.status === TreatmentPlanStatus.InProgress) {
      return;
    }

    await Firestore.patchDoc(plan.ref, {
      status: TreatmentPlanStatus.InProgress,
    });
  }

  static async buildAppointmentFromDetails(
    appointmentDetails: CreateAppointmentDetails,
    patient: WithRef<IPatient>,
    practitioner: INamedDocument<IStaffer>,
    appointmentRequest?: WithRef<IAppointmentRequest>,
    leaveCardHoldToApproval = false
  ): Promise<IAppointment> {
    const treatmentPlan =
      await AppointmentTreatmentPlanAssociator.getAssociatedTreatmentPlan(
        patient,
        appointmentDetails.treatment,
        appointmentDetails.overridePlan
      );

    if (!treatmentPlan) {
      throw new Error('No associated treatment plan found for appointment');
    }

    const requestDepositMetadata = appointmentRequest?.deposit?.metadata;

    return Appointment.init({
      treatmentPlan,
      practitioner,
      practice: toNamedDocument(appointmentDetails.practice),
      appointmentRequestRef: appointmentRequest?.ref,
      deposit:
        requestDepositMetadata && !leaveCardHoldToApproval
          ? {
              metadata: AppointmentCreator._metadataBeforeDepositInvoiceAttach(
                requestDepositMetadata
              ),
            }
          : undefined,
    });
  }

  static setEvent(
    appointment: IAppointment,
    patient: WithRef<IPatient>,
    event?: IEvent
  ): void {
    if (event) {
      event = Event.addParticipants(event, [
        {
          ...toNamedDocument(patient),
          type: ParticipantType.Patient,
        },
      ]);
    }

    Appointment.replaceEvent(appointment, event);
    if (isEventable(appointment)) {
      appointment.status = AppointmentStatus.Scheduled;
    }
  }

  static async setMetadata(
    appointment: IAppointment,
    tags: INamedDocument<ITag>[] = [],
    waitlistDetails?: IWaitListDetails
  ): Promise<void> {
    appointment.tags = tags;

    if (waitlistDetails) {
      const timezone = await TimezoneResolver.fromEvent(appointment);
      appointment.waitListItem = convertWaitListDetailsToItem(
        timezone,
        waitlistDetails
      );
    }
  }

  static getPractitioner(
    appointmentDetails: CreateAppointmentDetails,
    event?: IEvent
  ): INamedDocument<IStaffer> | undefined {
    if (event) {
      return event.organiser ?? toNamedDocument(Event.staff(event)[0]);
    }
    return isINamedDocument<IStaffer>(appointmentDetails.practitioner)
      ? appointmentDetails.practitioner
      : undefined;
  }

  static async getAppointment(
    appointmentDetails: CreateAppointmentDetails,
    patient: WithRef<IPatient>,
    practitioner: WithRef<IStaffer> | INamedDocument<IStaffer>,
    appointmentRequest?: WithRef<IAppointmentRequest>,
    leaveCardHoldToApproval = false
  ): Promise<IAppointment | WithRef<IAppointment>> {
    if (isTreatmentPlanWithBookableStep(appointmentDetails.treatment)) {
      const existingAppointment = await snapshot(
        TreatmentStep.appointment$(appointmentDetails.treatment.step)
      );
      if (existingAppointment) {
        return existingAppointment;
      }
    }
    return AppointmentCreator.buildAppointmentFromDetails(
      appointmentDetails,
      patient,
      stafferToNamedDoc(practitioner),
      appointmentRequest,
      leaveCardHoldToApproval
    );
  }

  static async addAutomations(
    appointment: WithRef<IAppointment>,
    automations: IAutomation<TreatmentStepAutomation>[]
  ): Promise<void> {
    if (!automations.length) {
      return;
    }
    await Appointment.addAutomations(appointment, automations);
  }

  static async addChecklistItems(
    appointment: WithRef<IAppointment>,
    checklists: IChecklistItem[]
  ): Promise<void> {
    if (!checklists.length) {
      return;
    }
    await Appointment.addChecklistItems(appointment, checklists);
  }

  /**
   * Persists deposit state onto an appointment, creating the backing deposit
   * invoice when the deposit is paid and enough context is available to bill it.
   *
   * Returns `retry` when another flow currently holds the invoice-creation
   * lease — the caller is expected to re-run once the lease expires rather than
   * risk issuing a second invoice for the same deposit.
   */
  static async setDepositOnAppointment(
    appointmentRef: DocumentReference<IAppointment>,
    depositMetadata: IDepositMetadata,
    patient?: WithRef<IPatient>,
    practice?: WithRef<IPractice>
  ): Promise<'complete' | 'retry'> {
    if (
      depositMetadata.status !== DepositStatus.Paid ||
      !patient ||
      !practice
    ) {
      await Firestore.patchDoc(appointmentRef, {
        deposit: { metadata: depositMetadata },
      });
      return 'complete';
    }

    const paidAt = depositMetadata.paidAt ?? toTimestamp();
    const claim = await AppointmentCreator.claimDepositInvoiceCreation(
      appointmentRef,
      depositMetadata
    );

    if (claim.status === DepositInvoiceClaimStatus.Leased) {
      /* eslint-disable-next-line no-console -- observability breadcrumb for double-invoice guard, this file has no logger dep. */
      console.info(
        `[appointment-creator] skipping deposit invoice creation for ${appointmentRef.path}: ${claim.status}`
      );
      return 'retry';
    }

    if (claim.status === DepositInvoiceClaimStatus.Exists) {
      return 'complete';
    }

    // Resolved out here because `DepositInvoice.create` owns its own transaction.
    const stripePayment = depositMetadata.paymentAttemptRef
      ? await AppointmentCreator._resolveStripeDepositPayment(
          depositMetadata.paymentAttemptRef,
          appointmentRef.path
        )
      : undefined;
    const payment: DepositPaymentProvenance = stripePayment?.payment ?? {
      provider: TransactionProvider.Manual,
    };
    const appointment = await Firestore.getDoc(appointmentRef);
    const description = await AppointmentCreator._resolveDepositDescription(
      depositMetadata,
      appointment,
      stripePayment?.depositRequestRef
    );

    const depositInvoiceRef = await DepositInvoice.create({
      patientRef: patient.ref,
      practiceRef: practice.ref,
      amountCents: depositMetadata.amountCents,
      currency: depositMetadata.currency,
      paidAt,
      payment,
      description,
      appointmentRef,
    });

    await Firestore.patchDoc(appointmentRef, {
      deposit: {
        metadata: {
          ...depositMetadata,
          paidAt,
        },
        invoiceRef: depositInvoiceRef,
      },
      depositInvoiceCreationClaimedAt: FieldValue.delete(),
    });
    return 'complete';
  }

  static async linkDepositInvoiceForManualRecord(
    appointmentRef: DocumentReference<IAppointment>,
    capturedInvoiceRef: DocumentReference<IInvoice>,
    claimedAt: Timestamp,
    paidMetadata: IDepositMetadata
  ): Promise<'complete' | 'retry'> {
    return Firestore.runTransaction(async (transaction) => {
      const current = await Firestore.getDoc(appointmentRef, transaction);
      if (!current.deposit) {
        throw new Error(
          `Appointment ${appointmentRef.path} has no deposit to link`
        );
      }
      if (current.deposit.invoiceRef) {
        if (!isSameRef(current.deposit.invoiceRef, capturedInvoiceRef)) {
          throw new Error(
            `Appointment ${appointmentRef.path} already links a different deposit invoice`
          );
        }
        return 'complete';
      }
      if (
        !current.depositInvoiceCreationClaimedAt ||
        !toMoment(current.depositInvoiceCreationClaimedAt).isSame(
          toMoment(claimedAt)
        )
      ) {
        return 'retry';
      }
      await Firestore.patchDoc(
        appointmentRef,
        {
          deposit: {
            ...current.deposit,
            metadata: paidMetadata,
            invoiceRef: capturedInvoiceRef,
          },
          depositInvoiceCreationClaimedAt: FieldValue.delete(),
        },
        transaction
      );
      return 'complete';
    });
  }

  /**
   * Centralises the lease boundary used by every invoice-creation guard so a
   * manual capture cannot drift from the automatic creation path's timing.
   */
  static hasActiveDepositInvoiceCreationLease(
    appointment: IAppointment
  ): boolean {
    const existingClaim = appointment.depositInvoiceCreationClaimedAt;
    return (
      !!existingClaim &&
      moment().diff(toMoment(existingClaim)) <
        invoiceClaimLease.asMilliseconds()
    );
  }

  /**
   * Takes the optimistic-concurrency lease guarding deposit invoice creation.
   *
   * The metadata argument identifies the deposit being claimed without letting
   * the lease write mutate deposit money state.
   */
  static async claimDepositInvoiceCreation(
    appointmentRef: DocumentReference<IAppointment>,
    _depositMetadata: IDepositMetadata
  ): Promise<DepositInvoiceClaimResult> {
    const claimedAt = toTimestamp();
    return Firestore.runTransaction(
      async (transaction: Transaction): Promise<DepositInvoiceClaimResult> => {
        const current = await Firestore.getDoc(appointmentRef, transaction);
        if (current.deposit?.invoiceRef) {
          return {
            status: DepositInvoiceClaimStatus.Exists,
            invoiceRef: current.deposit.invoiceRef,
          };
        }
        if (AppointmentCreator.hasActiveDepositInvoiceCreationLease(current)) {
          return { status: DepositInvoiceClaimStatus.Leased };
        }
        await Firestore.patchDoc(
          appointmentRef,
          {
            depositInvoiceCreationClaimedAt: claimedAt,
          },
          transaction
        );
        return { status: DepositInvoiceClaimStatus.Claimed, claimedAt };
      }
    );
  }

  /**
   * Releases only the caller's invoice-creation claim, leaving a replacement
   * owner untouched when an expired lease has already been reclaimed.
   */
  static async releaseDepositInvoiceCreationClaim(
    appointmentRef: DocumentReference<IAppointment>,
    claimedAt: Timestamp
  ): Promise<void> {
    await Firestore.runTransaction(async (transaction) => {
      const current = await Firestore.getDoc(appointmentRef, transaction);
      if (
        !current.depositInvoiceCreationClaimedAt ||
        !toMoment(current.depositInvoiceCreationClaimedAt).isSame(
          toMoment(claimedAt)
        )
      ) {
        return;
      }
      await Firestore.patchDoc(
        appointmentRef,
        {
          depositInvoiceCreationClaimedAt: FieldValue.delete(),
        },
        transaction
      );
    });
  }

  /**
   * Stamps the appointment's own practice onto every interaction it persists.
   * The appointment is the only place this context exists, so callers building
   * interactions upstream cannot supply it.
   */
  static async addInteractions(
    appointment: WithRef<IAppointment>,
    interactions: IInteractionV2[]
  ): Promise<void> {
    await asyncForEach(interactions, (interaction) =>
      Appointment.addInteraction(appointment, {
        ...interaction,
        practiceRef: appointment.practice.ref,
      })
    );
  }

  static async removeConflictingGapCandidates(
    appointment: WithRef<IAppointment>
  ): Promise<void> {
    if (!isEventable(appointment)) {
      return;
    }
    await removeConflictingCandidates(appointment.event);
  }

  static async add(
    practitioner: WithRef<IStaffer> | INamedDocument<IStaffer>,
    patient: WithRef<IPatient>,
    appointmentDetails: CreateAppointmentDetails,
    schedulingEventData: ISchedulingEventData,
    actor?: PrincipleCreatedBy,
    waitlistDetails?: IWaitListDetails,
    interactions: IInteractionV2[] = [],
    automations: IAutomation<TreatmentStepAutomation>[] = [],
    checklists: IChecklistItem[] = [],
    tags?: INamedDocument<ITag>[],
    event?: IEvent,
    appointmentRequest?: WithRef<IAppointmentRequest>,
    depositMetadata?: IDepositMetadata,
    canWriteCardHold = true
  ): Promise<WithRef<IAppointment>> {
    const leaveCardHoldToApproval =
      !canWriteCardHold &&
      AppointmentCreator._isRequestCardHold(appointmentRequest);
    const newAppointment = await AppointmentCreator.getAppointment(
      appointmentDetails,
      patient,
      practitioner,
      appointmentRequest,
      leaveCardHoldToApproval
    );

    AppointmentCreator.setEvent(newAppointment, patient, event);

    await AppointmentCreator.setMetadata(newAppointment, tags, waitlistDetails);
    if (depositMetadata && !leaveCardHoldToApproval) {
      newAppointment.deposit = {
        ...newAppointment.deposit,
        metadata:
          AppointmentCreator._metadataBeforeDepositInvoiceAttach(
            depositMetadata
          ),
      };
    }

    const appointment = await Patient.saveAppointment(
      patient,
      newAppointment,
      actor
    );
    if (!leaveCardHoldToApproval) {
      await AppointmentCreator._handleDepositTransfer(
        appointment,
        patient,
        appointmentDetails,
        appointmentRequest
      );
    }
    if (!appointmentRequest && depositMetadata?.status === DepositStatus.Paid) {
      await AppointmentCreator.setDepositOnAppointment(
        appointment.ref,
        depositMetadata,
        patient,
        appointmentDetails.practice
      );
    }
    await AppointmentCreator.addTreatmentPlanAssociation(
      appointment,
      appointmentDetails.duration ?? 0
    );
    await AppointmentCreator.syncPlanStatus(appointment);
    await AppointmentCreator.addChecklistItems(appointment, checklists);
    await AppointmentCreator.addInteractions(appointment, interactions);
    await AppointmentCreator.addSchedulingEvent(
      appointment,
      schedulingEventData,
      actor
    );
    await AppointmentCreator.addAutomations(appointment, automations);
    await AppointmentCreator.removeConflictingGapCandidates(appointment);

    return appointment;
  }

  static async addSchedulingEvent(
    appointment: WithRef<IAppointment>,
    schedulingEventData: ISchedulingEventData,
    scheduledByActor?: PrincipleCreatedBy
  ): Promise<void> {
    const eventAfter = SchedulingEvent.buildEventSnapshot(appointment);
    if (!SchedulingEvent.willCauseSchedulingEvent(undefined, eventAfter)) {
      return;
    }
    const schedulingEvent = SchedulingEvent.init({
      scheduledByActor,
      scheduledByStaffer:
        scheduledByActor?.type === MentionResourceType.Staffer
          ? (scheduledByActor.ref as DocumentReference<IStaffer>)
          : undefined,
      scheduledByPractice: schedulingEventData.scheduledByPractice,
      reason: schedulingEventData.reason,
      reasonSetManually: schedulingEventData.reasonSetManually,
      schedulingConditions: schedulingEventData.schedulingConditions,
      eventAfter,
    });
    await Appointment.addSchedulingEvent(
      appointment,
      schedulingEvent,
      scheduledByActor
    );
  }

  /**
   * Resolves ledger provenance for a deposit that was paid through a payment
   * attempt.
   *
   * An online deposit must be recorded against the provider reference that
   * actually moved the money, or the payment transaction can never be
   * reconciled against Stripe. A missing attempt or reference is therefore a
   * failed precondition — mirroring the capture guard in
   * `approveBookingRequestDeposit` — rather than a silent downgrade to a manual
   * payment, which would leave the ledger confidently wrong.
   */
  private static async _resolveStripeDepositPayment(
    paymentAttemptRef: DocumentReference<IDepositPaymentAttempt> | undefined,
    appointmentRefPath: string
  ): Promise<IResolvedStripeDepositPayment> {
    if (!paymentAttemptRef) {
      throw new Error(
        `Deposit payment attempt is missing for ${appointmentRefPath}`
      );
    }
    const paymentAttempt = await Firestore.getDoc(paymentAttemptRef);
    if (!paymentAttempt.providerReferenceId) {
      throw new Error(
        `Deposit payment provider reference is missing for ${appointmentRefPath}`
      );
    }
    return {
      payment: {
        provider: TransactionProvider.Stripe,
        reference: paymentAttempt.providerReferenceId,
      },
      depositRequestRef: paymentAttempt.depositRequestRef,
    };
  }

  private static async _resolveDepositDescription(
    depositMetadata: IDepositMetadata,
    appointment: IAppointment,
    depositRequestRef?: DocumentReference<IDepositRequest>
  ): Promise<string> {
    if (depositMetadata.description !== undefined) {
      return depositMetadata.description;
    }
    if (depositRequestRef) {
      const depositRequest = await Firestore.getDoc(depositRequestRef);
      return `Deposit for ${depositRequest.treatmentName}`;
    }
    const treatmentStepName = appointment.treatmentPlan.treatmentStep.name;
    return treatmentStepName ? `Deposit for ${treatmentStepName}` : 'Deposit';
  }

  private static async _handleDepositTransfer(
    appointment: WithRef<IAppointment>,
    patient: WithRef<IPatient>,
    appointmentDetails: CreateAppointmentDetails,
    appointmentRequest?: WithRef<IAppointmentRequest>
  ): Promise<void> {
    const requestDeposit = appointmentRequest?.deposit;
    if (!requestDeposit) {
      return;
    }

    const depositMetadata = requestDeposit.metadata;
    if (!depositMetadata) {
      return;
    }

    if (
      depositMetadata.status !== DepositStatus.Paid ||
      !depositMetadata.paidAt
    ) {
      await AppointmentCreator._linkAndReconcileDepositRequest(
        appointment,
        patient,
        appointmentDetails,
        depositMetadata,
        requestDeposit.requestRef
      );
      return;
    }

    await AppointmentCreator._transferPaidDeposit(
      appointment,
      patient,
      appointmentDetails,
      depositMetadata,
      depositMetadata.paidAt,
      requestDeposit.requestRef
    );
  }

  private static async _linkAndReconcileDepositRequest(
    appointment: WithRef<IAppointment>,
    patient: WithRef<IPatient>,
    appointmentDetails: CreateAppointmentDetails,
    depositMetadata: IDepositMetadata,
    depositRequestRef?: DocumentReference<IDepositRequest>
  ): Promise<void> {
    if (!depositRequestRef) {
      return;
    }

    try {
      await Firestore.patchDoc(depositRequestRef, {
        appointmentRef: appointment.ref,
        patientRef: patient.ref,
      });
    } catch (err) {
      /* eslint-disable-next-line no-console -- this file has no logger dep; manual reconciliation log must not rethrow and duplicate money movement. */
      console.error(
        {
          appointmentRefPath: appointment.ref.path,
          depositRequestRefPath: depositRequestRef.path,
          err,
        },
        '[CRITICAL] AppointmentCreator: Failed to patch depositRequest linkage — MANUAL RECONCILIATION REQUIRED'
      );
    }

    try {
      await AppointmentCreator._reconcilePaidDepositRequest(
        appointment,
        patient,
        appointmentDetails,
        depositMetadata,
        depositRequestRef
      );
    } catch (err) {
      /* eslint-disable-next-line no-console -- this file has no logger dep; manual reconciliation log must not rethrow and duplicate money movement. */
      console.error(
        {
          appointmentRefPath: appointment.ref.path,
          depositRequestRefPath: depositRequestRef.path,
          err,
        },
        '[CRITICAL] AppointmentCreator: Failed to reconcile paid DepositRequest after approval — MANUAL RECONCILIATION REQUIRED'
      );
    }
  }

  private static async _reconcilePaidDepositRequest(
    appointment: WithRef<IAppointment>,
    patient: WithRef<IPatient>,
    appointmentDetails: CreateAppointmentDetails,
    depositMetadata: IDepositMetadata,
    depositRequestRef: DocumentReference<IDepositRequest>
  ): Promise<void> {
    const linkedRequest =
      await Firestore.getDoc<IDepositRequest>(depositRequestRef);
    if (
      linkedRequest.status !== DepositRequestStatus.Paid ||
      !linkedRequest.paidAt
    ) {
      return;
    }

    const reconciledMetadata: IDepositMetadata = {
      ...depositMetadata,
      status: DepositStatus.Paid,
      paidAt: linkedRequest.paidAt,
      paymentAttemptRef: linkedRequest.paymentAttemptRef,
    };

    const claim = await AppointmentCreator.claimDepositInvoiceCreation(
      appointment.ref,
      reconciledMetadata
    );

    if (claim.status !== DepositInvoiceClaimStatus.Claimed) {
      /* eslint-disable-next-line no-console -- observability breadcrumb for double-invoice guard, this file has no logger dep. */
      console.info(
        `[appointment-creator] skipping deposit transfer reconciliation invoice creation for ${appointment.ref.path}: ${claim.status}`
      );
      return;
    }

    // Resolved out here because `DepositInvoice.create` owns its own transaction.
    const resolvedPayment =
      await AppointmentCreator._resolveStripeDepositPayment(
        linkedRequest.paymentAttemptRef,
        appointment.ref.path
      );

    const depositInvoiceRef = await DepositInvoice.create({
      patientRef: patient.ref,
      practiceRef: appointmentDetails.practice.ref,
      amountCents: depositMetadata.amountCents,
      currency: linkedRequest.currency,
      paidAt: linkedRequest.paidAt,
      payment: resolvedPayment.payment,
      description:
        depositMetadata.description ??
        `Deposit for ${linkedRequest.treatmentName}`,
      appointmentRef: appointment.ref,
    });

    await Firestore.patchDoc(appointment.ref, {
      deposit: {
        metadata: reconciledMetadata,
        invoiceRef: depositInvoiceRef,
      },
      depositInvoiceCreationClaimedAt: FieldValue.delete(),
    });

    try {
      await Firestore.patchDoc(depositRequestRef, { depositInvoiceRef });
    } catch (err) {
      /* eslint-disable-next-line no-console -- this file has no logger dep; manual reconciliation log must not rethrow and duplicate money movement. */
      console.error(
        {
          appointmentRefPath: appointment.ref.path,
          depositInvoiceRefPath: depositInvoiceRef.path,
          depositRequestRefPath: depositRequestRef.path,
          err,
        },
        '[CRITICAL] AppointmentCreator: Failed to patch depositRequest linkage — MANUAL RECONCILIATION REQUIRED'
      );
    }
  }

  private static async _transferPaidDeposit(
    appointment: WithRef<IAppointment>,
    patient: WithRef<IPatient>,
    appointmentDetails: CreateAppointmentDetails,
    depositMetadata: IDepositMetadata,
    paidAt: Timestamp,
    depositRequestRef?: DocumentReference<IDepositRequest>
  ): Promise<void> {
    const claim = await AppointmentCreator.claimDepositInvoiceCreation(
      appointment.ref,
      depositMetadata
    );

    if (claim.status !== DepositInvoiceClaimStatus.Claimed) {
      /* eslint-disable-next-line no-console -- observability breadcrumb for double-invoice guard, this file has no logger dep. */
      console.info(
        `[appointment-creator] skipping deposit transfer invoice creation for ${appointment.ref.path}: ${claim.status}`
      );
      return;
    }

    // Resolved out here because `DepositInvoice.create` owns its own transaction.
    const resolvedPayment =
      await AppointmentCreator._resolveStripeDepositPayment(
        depositMetadata.paymentAttemptRef,
        appointment.ref.path
      );
    const description = await AppointmentCreator._resolveDepositDescription(
      depositMetadata,
      appointment,
      resolvedPayment.depositRequestRef
    );

    const depositInvoiceRef = await DepositInvoice.create({
      patientRef: patient.ref,
      practiceRef: appointmentDetails.practice.ref,
      amountCents: depositMetadata.amountCents,
      currency: depositMetadata.currency,
      paidAt,
      payment: resolvedPayment.payment,
      description,
      appointmentRef: appointment.ref,
    });

    await Firestore.patchDoc(appointment.ref, {
      deposit: {
        metadata: depositMetadata,
        invoiceRef: depositInvoiceRef,
      },
      depositInvoiceCreationClaimedAt: FieldValue.delete(),
    });

    if (depositRequestRef) {
      try {
        await Firestore.patchDoc(depositRequestRef, {
          appointmentRef: appointment.ref,
          patientRef: patient.ref,
          depositInvoiceRef,
        });
      } catch (err) {
        /* eslint-disable-next-line no-console -- this file has no logger dep; manual reconciliation log must not rethrow and duplicate money movement. */
        console.error(
          {
            appointmentRefPath: appointment.ref.path,
            depositInvoiceRefPath: depositInvoiceRef.path,
            depositRequestRefPath: depositRequestRef.path,
            err,
          },
          '[CRITICAL] AppointmentCreator: Failed to patch depositRequest linkage — MANUAL RECONCILIATION REQUIRED'
        );
      }
    }
  }

  /**
   * Firestore rules only let staff with the deposit override write a card hold
   * to an appointment, whether as the authorisation or as the payment it
   * becomes once charged. For anyone else, `add` leaves a booking request's
   * hold off the new appointment, and `approveBookingRequestDeposit` copies it
   * across on the server, invoicing it if it has already been charged.
   */
  private static _isRequestCardHold(
    appointmentRequest?: IAppointmentRequest
  ): boolean {
    const requestMetadata = appointmentRequest?.deposit?.metadata;
    return (
      !!requestMetadata &&
      AppointmentCreator._metadataBeforeDepositInvoiceAttach(requestMetadata)
        .status === DepositStatus.Authorised
    );
  }

  private static _metadataBeforeDepositInvoiceAttach(
    depositMetadata: IDepositMetadata
  ): IDepositMetadata {
    if (depositMetadata.status !== DepositStatus.Paid) {
      return depositMetadata;
    }
    const metadata = { ...depositMetadata };
    delete metadata.paidAt;
    return {
      ...metadata,
      status:
        depositMetadata.paymentMode === DepositPaymentMode.PreAuth
          ? DepositStatus.Authorised
          : DepositStatus.Pending,
    };
  }
}
